import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserSession, UserRoles, LinkedCustomer } from '../models/classes';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private router: Router) { }

  // Using localStorage instead of sessionStorage so session is shared across browser tabs
  // This allows factory display to open in a new tab and still be authenticated
  setSession(session: UserSession): void {
    localStorage.setItem('sessionId', session.sessionId);
    localStorage.setItem('userId', session.userId.toString());
    localStorage.setItem('username', session.username);
    localStorage.setItem('email', session.email || '');
    localStorage.setItem('firstName', session.firstName || '');
    localStorage.setItem('lastName', session.lastName || '');
    localStorage.setItem('roles', JSON.stringify(session.roles));
    if (session.customerId) {
      localStorage.setItem('customerId', session.customerId.toString());
    }
    if (session.linkedCustomers) {
      localStorage.setItem('linkedCustomers', JSON.stringify(session.linkedCustomers));
    }
  }

  clearSession(): void {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('roles');
    localStorage.removeItem('customerId');
    localStorage.removeItem('linkedCustomers');
    localStorage.removeItem('isTvDevice');
  }

  // Set by the /tv-app pairing flow when the device picks up a session. Used
  // to gate the floating TV switcher so it only appears in actual TV
  // contexts, not when staff browse /factory-tv from a desk machine.
  markAsTvDevice(): void {
    localStorage.setItem('isTvDevice', '1');
  }

  isTvDevice(): boolean {
    return localStorage.getItem('isTvDevice') === '1';
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem('sessionId');
  }

  getSessionId(): string {
    return localStorage.getItem('sessionId') || '';
  }

  getUserId(): number {
    return parseInt(localStorage.getItem('userId') || '0', 10);
  }

  getUsername(): string {
    return localStorage.getItem('username') || '';
  }

  getFullName(): string {
    const firstName = localStorage.getItem('firstName') || '';
    const lastName = localStorage.getItem('lastName') || '';
    return `${firstName} ${lastName}`.trim() || this.getUsername();
  }

  getRoles(): string[] {
    const roles = localStorage.getItem('roles');
    return roles ? JSON.parse(roles) : [];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  isAdmin(): boolean {
    return this.hasRole(UserRoles.Admin);
  }

  isStaff(): boolean {
    return this.hasRole(UserRoles.Staff) || this.isAdmin();
  }

  isCustomer(): boolean {
    return this.hasRole(UserRoles.Customer);
  }

  isFactoryWorker(): boolean {
    return this.hasRole(UserRoles.FactoryWorker) || this.hasRole(UserRoles.FactoryManager) || this.isAdmin();
  }

  isDriver(): boolean {
    return this.hasRole(UserRoles.Driver) || this.isAdmin();
  }

  /** Every internal role — anyone who can owe a checklist task (not customers). */
  hasChecklist(): boolean {
    return ['Admin', 'Staff', 'FactoryManager', 'FactoryWorker', 'StockController', 'Driver'].some(r => this.hasRole(r));
  }

  /** Anyone with at least one report: staff/admin, the stock controller, the factory manager. */
  canViewReports(): boolean {
    return this.isStockController() || this.hasRole(UserRoles.FactoryManager);
  }

  /** Stock Controller role (purchasing & raw-material stock). Staff/Admin roll up. */
  isStockController(): boolean {
    return this.hasRole(UserRoles.StockController) || this.isStaff();
  }

  getCustomerId(): number | null {
    const customerId = localStorage.getItem('customerId');
    return customerId ? parseInt(customerId, 10) : null;
  }

  // Get all linked customers for this user
  getLinkedCustomers(): LinkedCustomer[] {
    const customers = localStorage.getItem('linkedCustomers');
    return customers ? JSON.parse(customers) : [];
  }

  // Check if user has multiple customer profiles
  hasMultipleCustomers(): boolean {
    return this.getLinkedCustomers().length > 1;
  }

  // Set the active customer for placing orders
  setActiveCustomer(customerId: number): void {
    localStorage.setItem('customerId', customerId.toString());
  }

  // Get the active customer details
  getActiveCustomer(): LinkedCustomer | null {
    const customerId = this.getCustomerId();
    if (!customerId) return null;
    return this.getLinkedCustomers().find(c => c.id === customerId) || null;
  }

  logout(): void {
    this.clearSession();
    this.router.navigate(['/login']);
  }
}
